import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '../Shared/Button';
import { Input } from '../Shared/Input';
import { Modal } from '../Shared/Modal';
import { useToast } from '../UI/ToastProvider';
import { documentService, type DocumentPickerNode } from '../../services/documentService';
import { templateService, type Template } from '../../services/templateService';
import { workflowService, type Workflow } from '../../services/workflowService';
import { workflowAssociationService, type WorkflowAssociationRuleDto } from '../../services/workflowAssociationService';

function getApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as AxiosError<{ message?: string }>).response?.data;
    if (data && typeof data === 'object' && data.message) {
      return String(data.message);
    }
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
}

function isUniquenessConflictMessage(message: string): boolean {
  return /já existe/i.test(message) || /regra ativa para/i.test(message);
}

function formatUniquenessUserMessage(
  dimension: 'extension' | 'template' | 'folder',
  rawMessage: string
): string {
  if (!isUniquenessConflictMessage(rawMessage)) {
    return rawMessage;
  }
  const label = dimension === 'extension' ? 'extensão' : dimension === 'template' ? 'template' : 'pasta';
  return `Já existe uma regra para esta ${label} nesta empresa. Edite a regra existente em vez de criar uma nova.`;
}

function normalizeExtensionInput(value: string): string {
  const t = value.trim().toLowerCase();
  if (!t) {
    return '';
  }
  return t.startsWith('.') ? t : `.${t}`;
}

function isValidExtension(value: string): boolean {
  if (!value) {
    return false;
  }
  if (!/^\..+/.test(value)) {
    return false;
  }
  if (value !== value.toLowerCase()) {
    return false;
  }
  return !/\s/.test(value);
}

function isGuidString(value: string): boolean {
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value.trim());
}

export type WorkflowRuleDimension = 'extension' | 'template' | 'folder';

export interface WorkflowAssociationRuleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  dimension: WorkflowRuleDimension;
  initialData?: WorkflowAssociationRuleDto;
  onSaved: () => void;
}

function isSystemTemplate(t: Template): boolean {
  return t.customerId == null || t.customerId === '' || t.customerId === '00000000-0000-0000-0000-000000000000';
}

export const WorkflowAssociationRuleFormModal: React.FC<WorkflowAssociationRuleFormModalProps> = ({
  isOpen,
  onClose,
  customerId,
  dimension,
  initialData,
  onSaved,
}) => {
  const { addToast } = useToast();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [folderNodes, setFolderNodes] = useState<DocumentPickerNode[]>([]);
  const [folderFilter, setFolderFilter] = useState('');

  const [workflowId, setWorkflowId] = useState('');
  const [extension, setExtension] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [folderId, setFolderId] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [extError, setExtError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const isEdit = Boolean(initialData?.id);

  const loadAux = useCallback(async () => {
    if (!isOpen) {
      return;
    }
    setLoading(true);
    setFormError(null);
    setExtError(null);
    try {
      const [wfs, tmpls] = await Promise.all([
        workflowService.getWorkflowsByCustomer(customerId),
        templateService.getTemplates(false),
      ]);
      setWorkflows(wfs.filter((w) => w.isActive));
      setTemplates(
        tmpls
          .filter((t) => t.isActive)
          .sort((a, b) => {
            const aSys = isSystemTemplate(a) ? 0 : 1;
            const bSys = isSystemTemplate(b) ? 0 : 1;
            if (aSys !== bSys) {
              return aSys - bSys;
            }
            return a.name.localeCompare(b.name, 'pt-BR');
          })
      );
      if (dimension === 'folder') {
        const nodes = await documentService.getFolderPickerNodes();
        setFolderNodes(nodes);
      } else {
        setFolderNodes([]);
      }
    } catch (e) {
      const m = getApiErrorMessage(e, 'Não foi possível carregar dados do formulário.');
      setFormError(m);
      addToast(m, 'error');
    } finally {
      setLoading(false);
    }
  }, [isOpen, customerId, dimension, addToast]);

  useEffect(() => {
    void loadAux();
  }, [loadAux]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (initialData) {
      setWorkflowId(initialData.workflowId);
      setExtension(initialData.extension ?? '');
      setTemplateId(initialData.templateId ?? '');
      setFolderId(initialData.folderId ?? '');
      setIsActive(initialData.isActive);
    } else {
      setWorkflowId('');
      setExtension('');
      setTemplateId('');
      setFolderId('');
      setIsActive(true);
    }
    setFolderFilter('');
    setExtError(null);
    setFormError(null);
  }, [isOpen, initialData, dimension]);

  const selectedFolderPath = useMemo(() => {
    if (!folderId) {
      return '';
    }
    return folderNodes.find((n) => n.id === folderId)?.fullPath ?? '';
  }, [folderId, folderNodes]);

  const filteredFolders = useMemo(() => {
    const q = folderFilter.trim().toLowerCase();
    if (!q) {
      return folderNodes;
    }
    return folderNodes.filter(
      (n) => n.title.toLowerCase().includes(q) || n.path.toLowerCase().includes(q) || n.fullPath.toLowerCase().includes(q)
    );
  }, [folderNodes, folderFilter]);

  const handleSave = async () => {
    if (!customerId) {
      setFormError('Cliente não identificado.');
      return;
    }
    if (!workflowId) {
      setFormError('Selecione um workflow.');
      return;
    }
    if (!isGuidString(workflowId)) {
      setFormError('O workflow selecionado está inválido. Recarregue a página e selecione novamente.');
      return;
    }

    if (dimension === 'extension') {
      const n = normalizeExtensionInput(extension);
      if (!isValidExtension(n)) {
        setExtError('Informe uma extensão começando com ponto, somente letras minúsculas (ex.: .pdf, .docx).');
        return;
      }
    }
    if (dimension === 'template' && !templateId) {
      setFormError('Selecione um template.');
      return;
    }
    if (dimension === 'folder' && !folderId) {
      setFormError('Selecione uma pasta.');
      return;
    }

    setFormError(null);
    setExtError(null);
    setSaving(true);
    try {
      const ext = dimension === 'extension' ? normalizeExtensionInput(extension) : undefined;
      if (isEdit && initialData?.id) {
        await workflowAssociationService.updateRule(initialData.id, {
          workflowId,
          isActive,
          extension: dimension === 'extension' ? ext : undefined,
          templateId: dimension === 'template' ? templateId : undefined,
          folderId: dimension === 'folder' ? folderId : undefined,
        });
        addToast('Regra atualizada com sucesso.', 'success');
      } else {
        await workflowAssociationService.createRule({
          customerId,
          workflowId,
          isActive,
          extension: dimension === 'extension' ? ext : undefined,
          templateId: dimension === 'template' ? templateId : undefined,
          folderId: dimension === 'folder' ? folderId : undefined,
        });
        addToast('Regra criada com sucesso.', 'success');
      }
      onSaved();
      onClose();
    } catch (e) {
      const raw = getApiErrorMessage(e, 'Não foi possível salvar a regra.');
      const msg = formatUniquenessUserMessage(dimension, raw);
      setFormError(msg);
      addToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const title = isEdit ? 'Editar regra de associação' : 'Nova regra de associação';
  const dimensionHelp =
    dimension === 'extension'
      ? 'Extensão do arquivo (ex.: .pdf).'
      : dimension === 'template'
        ? 'Template do documento.'
        : 'Pasta de destino (NodeType = pasta) na biblioteca.';

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={title}
      maxWidthClassName="sm:max-w-xl"
    >
      <p className="text-sm text-gray-600 -mt-1 mb-3 flex items-start gap-2">
        <InformationCircleIcon className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" aria-hidden />
        <span>{dimensionHelp} Apenas um critério por regra: escolhido na aba de origem.</span>
      </p>

      {formError && (
        <div className="mb-3 rounded-md bg-red-50 text-red-800 px-3 py-2 text-sm" role="alert">
          {formError}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" aria-label="Carregando" />
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Workflow *</label>
            <select
              className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
              value={workflowId}
              onChange={(e) => setWorkflowId(e.target.value)}
            >
              <option value="">Selecione…</option>
              {workflows.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                  {w.isSystem ? ' (Sistema)' : ''}
                </option>
              ))}
            </select>
          </div>

          {dimension === 'extension' && (
            <Input
              label="Extensão *"
              value={extension}
              onChange={(e) => {
                setExtension(e.target.value);
                setExtError(null);
              }}
              placeholder=".pdf"
              error={extError ?? undefined}
            />
          )}

          {dimension === 'template' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Template *</label>
              <select
                className="mt-1 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
              >
                <option value="">Selecione…</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} {isSystemTemplate(t) ? '— Sistema' : ''}
                  </option>
                ))}
              </select>
              {templates.length === 0 && (
                <p className="text-xs text-amber-700 mt-1">Nenhum template ativo encontrado.</p>
              )}
            </div>
          )}

          {dimension === 'folder' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Pasta *</label>
              <Input
                label="Filtrar pastas"
                value={folderFilter}
                onChange={(e) => setFolderFilter(e.target.value)}
                placeholder="Parte do caminho ou nome"
              />
              <select
                className="mt-2 block w-full border border-gray-300 rounded-md py-2 px-3 text-sm min-h-[120px]"
                size={8}
                value={folderId}
                onChange={(e) => setFolderId(e.target.value)}
              >
                <option value="">Selecione uma pasta…</option>
                {filteredFolders.map((n) => (
                  <option key={n.id} value={n.id} title={n.fullPath}>
                    {n.fullPath || n.path}
                  </option>
                ))}
              </select>
              {selectedFolderPath ? (
                <p className="text-xs text-gray-600 mt-1">
                  Selecionado: <span className="font-mono text-gray-800">{selectedFolderPath}</span>
                </p>
              ) : null}
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              id="rule-active"
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <label htmlFor="rule-active" className="text-sm text-gray-800">
              Ativa
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={() => void handleSave()} disabled={saving || loading}>
              {saving ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
